// Pure canvas-compositor for the View-on-Wall room preview.
//
// Loads the room photo + the artwork image and renders a single composite
// PNG blob that mirrors exactly what the user sees on-screen:
//
//   1. Room photo as the base layer (full bleed).
//   2. Drop-shadowed frame moulding (optional, controlled by framePreset).
//   3. Cream mat with print sitting inside (or print directly if no frame).
//
// All dimensions are derived from real-world cm via the user-calibrated
// `wallWidthCm` so framed pieces grow outward without distorting the print.
// Exported as a function so it can be unit-tested without React.
//
// Returns a Promise<Blob> resolving to an `image/png` blob ready for
// FormData uploads to /api/visualizer/email-preview.

const FRAME_GRADIENTS = {
  black:   ['#1f1f1f', '#2a2a2a', '#0e0e0e'],
  white:   ['#fafafa', '#ffffff', '#e8e8e8'],
  natural: ['#e6c79a', '#d4ad7b', '#b08157'],
  gold:    ['#e6c66c', '#c9a247', '#9a7727'],
};

const drawFrameFill = (ctx, framePreset, fLeft, fTop, totalW, totalH) => {
  const g = ctx.createLinearGradient(fLeft, fTop, fLeft + totalW, fTop + totalH);
  const stops = FRAME_GRADIENTS[framePreset];
  if (stops) {
    g.addColorStop(0, stops[0]);
    g.addColorStop(0.45, stops[1]);
    g.addColorStop(1, stops[2]);
  } else {
    g.addColorStop(0, '#000');
    g.addColorStop(1, '#000');
  }
  ctx.fillStyle = g;
};

const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => resolve(img);
  img.onerror = (e) => reject(e);
  img.src = src;
});

const composePreview = async ({
  roomImage,
  artworkSrc,
  artworkPosition,   // { x: 0-100, y: 0-100 }
  artworkScale,      // multiplier on the cm-derived size
  wallWidthCm,       // user-calibrated wall width in the room photo
  dims,              // { width, height } in cm
  frame,             // { matCm, frameCm }
  framePreset,       // 'none' | 'black' | 'white' | 'natural' | 'gold'
}) => {
  if (!roomImage) throw new Error('No room image');

  const room = await loadImage(roomImage);
  const W = room.naturalWidth;
  const H = room.naturalHeight;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(room, 0, 0, W, H);

  const art = await loadImage(artworkSrc);

  // Pixel-perfect mirror of the on-screen render. All sizes derived
  // from real-world cm via the user-calibrated wall width.
  const pxPerCm = W / wallWidthCm;
  const artW    = dims.width  * pxPerCm * artworkScale;
  const artH    = dims.height * pxPerCm * artworkScale;
  const matPx   = frame.matCm   * pxPerCm * artworkScale;
  const framePx = frame.frameCm * pxPerCm * artworkScale;
  const totalW = artW + 2 * (matPx + framePx);
  const totalH = artH + 2 * (matPx + framePx);
  const cx = (artworkPosition.x / 100) * W;
  const cy = (artworkPosition.y / 100) * H;
  const fLeft = cx - totalW / 2;
  const fTop  = cy - totalH / 2;

  if (framePx > 0 || matPx > 0) {
    // Drop shadow under the whole framed piece
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.4)';
    ctx.shadowBlur = Math.max(14, totalW * 0.03);
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = Math.max(8, totalW * 0.018);
    drawFrameFill(ctx, framePreset, fLeft, fTop, totalW, totalH);
    ctx.fillRect(fLeft, fTop, totalW, totalH);
    ctx.restore();
    // Mat (cream archival board)
    ctx.fillStyle = '#f8f4ea';
    ctx.fillRect(fLeft + framePx, fTop + framePx,
                 totalW - 2 * framePx, totalH - 2 * framePx);
    // Print sitting on the mat (object-contain centred)
    const innerX = fLeft + framePx + matPx;
    const innerY = fTop  + framePx + matPx;
    const ir = art.naturalWidth / art.naturalHeight;
    const cr = artW / artH;
    let drawW = artW;
    let drawH = artH;
    let drawX = innerX;
    let drawY = innerY;
    if (ir > cr) { drawH = artW / ir; drawY = innerY + (artH - drawH) / 2; }
    else         { drawW = artH * ir; drawX = innerX + (artW - drawW) / 2; }
    // Subtle drop shadow under the print
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.18)';
    ctx.shadowBlur  = Math.max(4, artW * 0.012);
    ctx.shadowOffsetY = Math.max(1, artW * 0.004);
    ctx.drawImage(art, drawX, drawY, drawW, drawH);
    ctx.restore();
  } else {
    ctx.drawImage(art, fLeft, fTop, totalW, totalH);
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Canvas export failed'));
    }, 'image/png', 0.92);
  });
};

export default composePreview;
